import { gql, useQuery } from '@apollo/client'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import {
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
} from 'services/content-references/types'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'

const CONTENT_REFERENCE_TYPE_OPTIONS_QUERY = gql`
  query ContentReferenceTypeOptionList(
    $offset: Int = 0
    $limit: Int = 500
    $order: [[String]] = [["title", "asc"]]
    $where: ContentWhereInput = {}
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(offset: $offset, limit: $limit, order: $order, where: $where, language: $language) {
      content {
        language
        titleGroupId
        title
      }
    }
  }
`

function buildQueryVariables (type, languageSelection) {
  const contentReferenceTypeData = getContentReferenceTypesContentDictionary().get(type)
  const contentType = { eq: contentReferenceTypeData.getIn(['contentType', 0]) }
  let bundle
  let filterType
  let fallbackToPrimaryLanguage = false
  switch (type) {
    case CONTENT_REFERENCE_TYPE_SERIES:
    case CONTENT_REFERENCE_TYPE_PRODUCT:
    case CONTENT_REFERENCE_TYPE_PRIMARY_COURSE:
      break
    default:
      fallbackToPrimaryLanguage = true
      bundle = contentReferenceTypeData.has('bundle')
        ? { eq: contentReferenceTypeData.get('bundle') }
        : undefined
      filterType = contentReferenceTypeData.has('filterType')
        ? { like: contentReferenceTypeData.get('filterType') }
        : undefined
      break
  }
  return {
    order: [['title', 'asc']],
    where: _omitBy(
      {
        contentType,
        bundle,
        filterType,
        language: !fallbackToPrimaryLanguage ? { in: languageSelection } : undefined,
      },
      _isUndefined,
    ),
    language: fallbackToPrimaryLanguage ? [...languageSelection, '_primary'] : undefined,
  }
}

const queryOptions = { notifyOnNetworkStatusChange: true }

/**
 * Fetches dropdown options for all content reference types used by the content list grid.
 * Returns an object keyed by content reference type constant (e.g. CONTENT_REFERENCE_TYPE_SERIES)
 * with value = array of { language, titleGroupId, title }.
 *
 * @param {string[]} languageSelection - e.g. ['en']
 * @returns {Object} Map of type constant -> options array
 */
export function useContentReferenceOptions (languageSelection) {
  const { data: seriesData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_SERIES, languageSelection),
    },
    ...queryOptions,
  })
  const { data: rainbowCategoryData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY, languageSelection),
    },
    ...queryOptions,
  })
  const { data: productData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_PRODUCT, languageSelection),
    },
    ...queryOptions,
  })
  const { data: primaryCourseData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_PRIMARY_COURSE, languageSelection),
    },
    ...queryOptions,
  })
  const { data: genreData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_GENRES, languageSelection),
    },
    ...queryOptions,
  })
  const { data: thirdPartyGenreData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE, languageSelection),
    },
    ...queryOptions,
  })
  const { data: contentOriginData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN, languageSelection),
    },
    ...queryOptions,
  })
  const { data: centersData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_CENTERS, languageSelection),
    },
    ...queryOptions,
  })
  const { data: categoryData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_CATEGORY, languageSelection),
    },
    ...queryOptions,
  })
  const { data: marketingCentersData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MARKETING_CENTERS, languageSelection),
    },
    ...queryOptions,
  })
  const { data: meditationFocusData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS, languageSelection),
    },
    ...queryOptions,
  })
  const { data: meditationStyleData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_STYLE, languageSelection),
    },
    ...queryOptions,
  })
  const { data: meditationTeacherData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER, languageSelection),
    },
    ...queryOptions,
  })
  const { data: meditationDurationData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_DURATION, languageSelection),
    },
    ...queryOptions,
  })
  const { data: yogaDurationData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_DURATION, languageSelection),
    },
    ...queryOptions,
  })
  const { data: yogaFocusData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_FOCUS, languageSelection),
    },
    ...queryOptions,
  })
  const { data: yogaLevelData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_LEVEL, languageSelection),
    },
    ...queryOptions,
  })
  const { data: yogaStyleData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_STYLE, languageSelection),
    },
    ...queryOptions,
  })
  const { data: yogaTeacherData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_TEACHER, languageSelection),
    },
    ...queryOptions,
  })
  const { data: interviewGuestData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST, languageSelection),
    },
    ...queryOptions,
  })
  const { data: interviewHostData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_INTERVIEW_HOST, languageSelection),
    },
    ...queryOptions,
  })

  return {
    [CONTENT_REFERENCE_TYPE_SERIES]: seriesData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY]: rainbowCategoryData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_PRODUCT]: productData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_PRIMARY_COURSE]: primaryCourseData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_GENRES]: genreData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE]: thirdPartyGenreData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN]: contentOriginData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_CENTERS]: centersData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_CATEGORY]: categoryData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_MARKETING_CENTERS]: marketingCentersData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS]: meditationFocusData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_MEDITATION_STYLE]: meditationStyleData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER]: meditationTeacherData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_MEDITATION_DURATION]:
      meditationDurationData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_YOGA_DURATION]: yogaDurationData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_YOGA_FOCUS]: yogaFocusData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_YOGA_LEVEL]: yogaLevelData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_YOGA_STYLE]: yogaStyleData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_YOGA_TEACHER]: yogaTeacherData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST]: interviewGuestData?.contentList?.content || [],
    [CONTENT_REFERENCE_TYPE_INTERVIEW_HOST]: interviewHostData?.contentList?.content || [],
  }
}
